import { getConfig } from '../../../../../config/envConfig';
import { withForwardedEnv } from '../../../../../config/forwardedEnv';
import type { PolicyCheckData, PolicyDomain } from './policyTypes';

/** Path for the workspace validation + policy endpoint on oauth-server. */
export const WORKSPACE_POLICY_PATH = '/consent/workspace';

/**
 * Parameters for the workspace policy endpoint.
 * `GET /consent/workspace?context=<jwt>&selectedWorkspace=<workspace>`
 */
export type WorkspacePolicyParams = {
  /** The raw context JWT string from the URL. */
  contextJwt: string;
  /** The workspace identifier selected by the user. */
  selectedWorkspace: string;
};

export type PolicyCheckSuccess = { ok: true; data: PolicyCheckData };
export type PolicyCheckFailure = {
  ok: false;
  errorMessage: string;
  status?: number;
};
export type PolicyCheckResult = PolicyCheckSuccess | PolicyCheckFailure;

// ---------------------------------------------------------------------------
// URL builders
// ---------------------------------------------------------------------------

function buildWorkspacePolicyUrl(
  baseUrl: string,
  params: WorkspacePolicyParams,
): string {
  const root = apiBaseUrlRoot(baseUrl);
  const url = new URL(
    WORKSPACE_POLICY_PATH,
    root.endsWith('/') ? root : `${root}/`,
  );
  url.searchParams.set('context', params.contextJwt);
  url.searchParams.set('selectedWorkspace', params.selectedWorkspace);

  return withForwardedEnv(url.toString());
}

function apiBaseUrlRoot(baseUrl: string): string {
  return baseUrl.trim() || window.location.origin;
}

// ---------------------------------------------------------------------------
// Domain parsing (shared)
// ---------------------------------------------------------------------------

function parseDomain(rawDomain: unknown): PolicyDomain {
  const o =
    rawDomain && typeof rawDomain === 'object'
      ? (rawDomain as Record<string, unknown>)
      : {};
  return {
    // Fail-closed: if the domain field is missing or malformed, default to
    // blocked so a corrupted API response never silently permits a redirect.
    allowed: typeof o.allowed === 'boolean' ? o.allowed : false,
    error: typeof o.error === 'boolean' ? o.error : false,
  };
}

// ---------------------------------------------------------------------------
// Workspace policy check  (GET /consent/workspace)
// ---------------------------------------------------------------------------

/**
 * Fetches workspace policy from the unified oauth-server endpoint:
 * `GET /consent/workspace?context=<jwt>&selectedWorkspace=<workspace>`
 *
 * Parses the shared `domain` field and returns the raw response body
 * so callers (MCP) can parse additional fields like `toolsets`.
 *
 * For A2A, the response will not contain `toolsets` — only `domain` is relevant.
 */
export async function fetchWorkspacePolicyCheck(
  params: WorkspacePolicyParams,
  signal?: AbortSignal,
): Promise<PolicyCheckResult> {
  const policyUrl = buildWorkspacePolicyUrl(
    getConfig().mcpPolicy.apiBaseUrl,
    params,
  );
  let res: Response;
  try {
    res = await fetch(policyUrl, {
      method: 'GET',
      credentials: 'include',
      signal,
    });
  } catch (e) {
    if (
      signal?.aborted ||
      (e instanceof DOMException && e.name === 'AbortError')
    ) {
      throw e;
    }
    const msg = e instanceof Error ? e.message : 'Network error';
    return { ok: false, errorMessage: msg };
  }

  if (!res.ok) {
    return {
      ok: false,
      errorMessage: `Workspace policy check failed (${res.status})`,
      status: res.status,
    };
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, errorMessage: 'Invalid workspace policy response' };
  }

  if (!body || typeof body !== 'object') {
    return { ok: false, errorMessage: 'Invalid policy response' };
  }

  const o = body as Record<string, unknown>;

  if (o.success === false) {
    return { ok: false, errorMessage: 'Policy check unsuccessful' };
  }

  const domain = parseDomain(o.domain);
  // a2aEnabled sits at the same level as domain in the API response.
  // Fail-closed: default to true so non-A2A flows are unaffected.
  const a2aEnabled = typeof o.a2aEnabled === 'boolean' ? o.a2aEnabled : true;
  const data: PolicyCheckData = {
    domain,
    redirectAllowed: domain.allowed && !domain.error,
    a2aEnabled,
    rawBody: o,
  };

  return { ok: true, data };
}
